import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useTheme as useThemeContext, THEMES } from '../../contexts/ThemeContext';
import { config } from '../../config/environment';

const BANNER_DISMISSED_KEY = 'betaModeBannerDismissed';

const BetaModeBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const muiTheme = useMuiTheme();
  const { theme: currentThemeName } = useThemeContext();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Determine banner background color based on theme
  // Use the exact primary colors from each theme
  const getBannerColor = () => {
    switch (currentThemeName) {
      case THEMES.LIGHT:
        // Sage green - same as light theme primary color
        return muiTheme.palette.primary.main; // #87AE73
      case THEMES.OASIS:
        // Light blue - same as oasis theme primary color
        return muiTheme.palette.primary.main; // #87CEEB
      case THEMES.DARK:
      default:
        // Yellow - matches the dark theme's primary color
        return muiTheme.palette.primary.main;
    }
  };

  const bannerColor = getBannerColor();

  useEffect(() => {
    // Check if banner was dismissed in this session (not persisted across sessions)
    const dismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
    setIsDismissed(dismissed);
  }, []);

  // Show banner in staging OR production (we're still in beta mode)
  // Check config, hostname, and port
  const shouldShowBanner = (() => {
    if (config.isStaging) return true;
    
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const port = window.location.port;
      
      // Show in staging
      if (hostname === 'staging.tuneswell.com') return true;
      if (hostname === 'localhost' && (port === '3001' || port === '3000')) return true;
      
      // Show in production (tuneswell.com) - we're still in beta
      if (hostname === 'tuneswell.com' || hostname === 'www.tuneswell.com') return true;
    }
    
    return false;
  })();

  const handleDismiss = () => {
    // Use sessionStorage so dismissal only persists for current session
    // Banner will reappear on next login/session until we switch to real money
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setIsDismissed(true);
  };

  // Don't render if banner shouldn't show or if dismissed
  if (!shouldShowBanner || isDismissed) {
    return null;
  }

  // Header height: 52px mobile, 60px desktop
  const headerHeight = isMobile ? 52 : 60;

  return (
    <Box 
      sx={{ 
        position: 'fixed', 
        top: `${headerHeight}px`, 
        left: 0, 
        right: 0, 
        zIndex: 1200, // Above header (AppBar default is 1100) but below menus (1300) so theme dropdown is accessible
        backgroundColor: bannerColor, // Theme-aware background color
        color: '#000', // Black text
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '8px 16px',
        minHeight: '48px',
      }}
    >
      <Typography 
        variant="body2" 
        sx={{
          flex: 1,
          textAlign: 'left',
          color: '#000',
          fontWeight: 500,
          paddingRight: 1,
        }}
      >
        Tuneswell is in beta mode for testing! Our payment system is using fake money for credits and earnings. Enjoy free listening while you can, it won't last long.
      </Typography>
      <IconButton
        size="small"
        onClick={handleDismiss}
        sx={{
          color: '#000',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }
        }}
        aria-label="dismiss banner"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default BetaModeBanner;

